import { config } from './config';

interface Payload {
  input: string;
  voice_id: string;
  model: string;
  language: string;
}

const audioMIMEType = 'audio/mpeg';
interface StreamRequest {
  input: string;
  voice_id: string;
  language: string | undefined;
  model: string;
}

const handleResponseErrors = async (response: Response): Promise<void> => {
  // Prevent error handling for 2xx responses
  if (response.ok) return;

  const errorText = await response.text();
  switch (response.status) {
    case 402:
      throw new Error(
        'The account balance is insufficient to complete the request.',
      );
    case 400:
      throw new Error(`Bad request: ${errorText}`);
    case 401:
      throw new Error('Unauthorized: Ensure your API key is correct.');
    case 500:
      throw new Error('Server error: Please try again later.');
    default:
      throw new Error(`Error ${response.status}: ${errorText}`);
  }
};

async function streamSpeech(
  apiKey: string,
  payload: StreamRequest,
  format: 'audio/mpeg' | 'audio/ogg' | 'audio/aac',
): Promise<ReadableStream<Buffer>> {
  if (!apiKey) {
    throw new Error('API key is required');
  }
  const resp = await fetch(
    `${config.swsApiBaseUrl}/v1/audio/stream`,
    {
      method: 'POST',
      headers: {
        authorization: `Bearer ${apiKey}`,
        accept: format,
      },
      body: JSON.stringify(payload),
    },
  );

  if (!resp.ok) await handleResponseErrors(resp);

  return resp.body as ReadableStream<Buffer>;
}

const getAudioBlobUrlFromStream = (
  response: ReadableStream<Buffer>
) => {
  const reader = response.getReader();
  const chunks: Buffer[] = [];

  // Read the entire stream
  const readStream = async () => {
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }

      const blob = new Blob(chunks, { type: audioMIMEType });
      const audioUrl = URL.createObjectURL(blob);
      return audioUrl;
    } catch (error) {
      console.error('Error reading audio stream:', error);
      throw error;
    }
  };

  return readStream();
};

const getStreamingAudioURL = (
  response: ReadableStream<Buffer>,
) => {
  // Check if MediaSource is supported
  const isMediaSourceSupported = typeof MediaSource === 'undefined' || !MediaSource.isTypeSupported('audio/mpeg');
  if (isMediaSourceSupported) {
    return getAudioBlobUrlFromStream(response)
  }

  
  // eslint-disable-next-line compat/compat
  const mediaSource = new MediaSource();
  const audioUrl = URL.createObjectURL(mediaSource);

  mediaSource.addEventListener('sourceopen', async () => {
    const sourceBuffer = mediaSource.addSourceBuffer(audioMIMEType);
    const reader = response.getReader();
    const chunks = [];
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        mediaSource.endOfStream();
        break;
      } else {
        const p = new Promise((resolve) => {
          sourceBuffer.onupdateend = resolve;
        });
        sourceBuffer.appendBuffer(value);
        chunks.push(value);
        await p;
      }
    }
  });

  return audioUrl;
};

export const requestAudioAPI = async (
  apiKey: string,
  payload: Payload,
): Promise<string> => {
  const readable = await streamSpeech(
    apiKey,
    {
      ...payload,
    },
    audioMIMEType,
  );
  return getStreamingAudioURL(readable);
};
